import React, { useMemo, useState, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Button as MuiButton,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import Select from "components/select";

export const getLikertPoints = (likertConfig = {}) => {
  const min = Number(likertConfig.min);
  const max = Number(likertConfig.max);
  const step = Number(likertConfig.step || 1);

  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(step) || step <= 0 || max < min) {
    return [];
  }

  const points = [];
  for (let value = min; value <= max + 1e-9; value += step) {
    points.push(Number(value.toFixed(6)));
  }
  return points;
};

const getScaleLabel = (labels = [], index, point) => {
  if (Array.isArray(labels) && labels[index]) {
    return labels[index];
  }
  return String(point);
};

const LikertScaleQuestion = ({ options, componentRef, likert, langMap, peerNominationEnabled }) => {
  const points = useMemo(() => getLikertPoints(likert), [likert]);
  const labels = Array.isArray(likert?.labels) ? likert.labels : [];

  const [ratings, setRatings] = useState([{ user: null, weight: null }]);
  const [singleWeight, setSingleWeight] = useState(null);

  useImperativeHandle(componentRef, () => ({
    getState: () => {
      if (!peerNominationEnabled) {
        return singleWeight === null ? [] : [{ weight: Number(singleWeight) }];
      }
      return ratings
        .filter((row) => row.user || row.weight !== null)
        .map((row) => ({ user: row.user, weight: row.weight }));
    },
  }));

  const usedIds = ratings
    .map((row) => row.user?.value)
    .filter(Boolean)
    .map((id) => String(id));

  const handleUserChange = (index, selected) => {
    const next = [...ratings];
    next[index] = { ...next[index], user: selected?.[0] || null };
    setRatings(next);
  };

  const handleWeightChange = (index, point) => {
    const next = [...ratings];
    next[index] = { ...next[index], weight: Number(point) };
    setRatings(next);
  };

  const addRatingRow = () => {
    setRatings((prev) => [...prev, { user: null, weight: null }]);
  };

  const removeRatingRow = (index) => {
    setRatings((prev) => prev.filter((_, idx) => idx !== index));
  };

  const renderLikertBubbleRow = (weight, onChangeWeight, rowKeyPrefix) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          {points.map((point, pointIndex) => (
            <TableCell align="center" key={`header-${rowKeyPrefix}-${point}`}>
              {getScaleLabel(labels, pointIndex, point)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          {points.map((point) => (
            <TableCell align="center" key={`point-${rowKeyPrefix}-${point}`}>
              <Radio
                checked={weight === Number(point)}
                onChange={() => onChangeWeight(point)}
                value={String(point)}
                color="primary"
                inputProps={{ "aria-label": `${langMap.LIKERT_SCORE_LABEL} ${point}` }}
              />
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );

  if (!peerNominationEnabled) {
    return (
      <>
        <Typography variant="body2" style={{ marginBottom: "8px" }}>
          {langMap.LIKERT_HINT}
        </Typography>
        {renderLikertBubbleRow(singleWeight, (point) => setSingleWeight(Number(point)), "self")}
      </>
    );
  }

  return (
    <>
      <Typography variant="body2" style={{ marginBottom: "8px" }}>
        {langMap.LIKERT_HINT}
      </Typography>

      {ratings.map((row, idx) => {
        const rowOptions = options.filter((option) => {
          const optionId = String(option.value);
          const currentRowId = row.user ? String(row.user.value) : null;
          return !usedIds.includes(optionId) || optionId === currentRowId;
        });

        return (
          <Grid key={idx} container direction="column" style={{ marginBottom: "16px" }}>
            <Grid item style={{ marginBottom: "8px" }}>
              <Select
                options={rowOptions}
                onChange={(selected) => handleUserChange(idx, selected)}
                onInputChange={() => {}}
                value={row.user ? [row.user] : []}
                isMulti={false}
                placeholder={langMap.SELECT_INPUT}
              />
            </Grid>

            <Grid item>
              {renderLikertBubbleRow(row.weight, (point) => handleWeightChange(idx, point), String(idx))}
            </Grid>

            <Grid item style={{ marginTop: "8px" }}>
              {ratings.length > 1 ? (
                <MuiButton color="secondary" variant="outlined" size="small" onClick={() => removeRatingRow(idx)}>
                  -
                </MuiButton>
              ) : (
                <div style={{ width: "64px" }} />
              )}
            </Grid>
          </Grid>
        );
      })}

      <MuiButton color="primary" variant="contained" disabled={ratings.length >= options.length} onClick={addRatingRow}>
        +
      </MuiButton>
    </>
  );
};

LikertScaleQuestion.propTypes = {
  options: PropTypes.array.isRequired,
  componentRef: PropTypes.any.isRequired,
  likert: PropTypes.object,
  langMap: PropTypes.object.isRequired,
  peerNominationEnabled: PropTypes.bool,
};

LikertScaleQuestion.defaultProps = {
  likert: {},
  peerNominationEnabled: false,
};

export default LikertScaleQuestion;
